import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useAppSelector, useAppDispatch } from './redux';
import { ServerMessagingProvider } from '../services/messaging/providers/ServerMessagingProvider';
import { IMessage } from '../services/messaging/IMessagingProvider';
import { NotificationService } from '../utils/notifications';
import { encryptMessage, getKeyPair } from '../services/encryption';
import {
  setConnected,
  setConversations,
  setMessages,
  addMessage,
  setUserOnline,
  setUserOffline,
  markConversationAsRead
} from '../store/reducers/messages.reducer';

const API_BASE = process.env.REACT_APP_API_BASE_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8080');

export const useMessaging = () => {
  const dispatch = useAppDispatch();
  const { loginUser } = useAppSelector((state) => state.user);
  const { conversations, currentConversation, messages, isConnected } = useAppSelector((state) => state.messages);
  const [provider, setProvider] = useState<ServerMessagingProvider | null>(null);
  const [error, setError] = useState<string | null>(null);
  const providerRef = useRef<ServerMessagingProvider | null>(null);

  // Initialize messaging provider
  useEffect(() => {
    if (!loginUser?.id || providerRef.current) return;

    const initProvider = async () => {
      try {
        const senderName = loginUser.display_name || loginUser.email || 'Someone';
        const newProvider = new ServerMessagingProvider(String(loginUser.id), senderName);
        providerRef.current = newProvider;

        await newProvider.connect();
        setProvider(newProvider);
        dispatch(setConnected(true));

        // Request notification permission
        await NotificationService.requestPermission();

        // Load conversations
        const convs = await newProvider.getAllConversations();
        dispatch(setConversations(convs));

        // Listen for new messages
        newProvider.onMessageReceived((message: IMessage) => {
          dispatch(addMessage({ ...message, currentUserId: String(loginUser.id) }));
        });

        // Listen for online status changes
        newProvider.onUserOnlineStatusChanged((userId: string, isOnline: boolean) => {
          if (isOnline) {
            dispatch(setUserOnline(userId));
          } else {
            dispatch(setUserOffline(userId));
          }
        });
      } catch (err: any) {
        console.error('Failed to initialize messaging:', err);
        setError(err.message);
        dispatch(setConnected(false));
      }
    };

    initProvider();

    return () => {
      if (providerRef.current) {
        providerRef.current.disconnect();
        providerRef.current = null;
      }
    };
  }, [loginUser, dispatch]);

  const sendMessage = useCallback(async (recipientId: string, content: string, mediaData?: { mediaType: string; mediaUrl: string; thumbnailUrl?: string; mediaKey?: string; mediaMimeType?: string; linkPreview?: any }) => {
    if (!providerRef.current) throw new Error('Messaging not initialized');
    if (!loginUser?.id) throw new Error('User not logged in');

    try {
      const message = await providerRef.current.sendMessage(recipientId, content, mediaData);
      dispatch(addMessage({ ...message, currentUserId: String(loginUser.id) }));
      return message;
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, [dispatch, loginUser]);

  const loadMessages = useCallback(async (userId: string) => {
    if (!providerRef.current) return;

    try {
      const msgs = await providerRef.current.getMessages(userId);
      dispatch(setMessages({ userId, messages: msgs }));
    } catch (err: any) {
      setError(err.message);
    }
  }, [dispatch]);

  const markAsRead = useCallback(async (userId: string) => {
    if (!providerRef.current) return;

    const userMessages = messages[userId] || [];
    for (const msg of userMessages) {
      if (msg.recipientId === String(loginUser?.id) && msg.status !== 'read') {
        await providerRef.current.markAsRead(msg.id);
      }
    }

    dispatch(markConversationAsRead(userId));
  }, [messages, loginUser, dispatch]);

  const isUserOnline = useCallback(async (userId: string): Promise<boolean> => {
    if (!providerRef.current) return false;
    return await providerRef.current.isOnline(userId);
  }, []);

  const deleteConversation = useCallback(async (userId: string) => {
    if (!providerRef.current) throw new Error('Messaging not initialized');

    try {
      await providerRef.current.deleteConversation(userId);
      // Reload conversations after deletion
      const convs = await providerRef.current.getAllConversations();
      dispatch(setConversations(convs));
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, [dispatch]);

  // Periodically reload current conversation so expired messages disappear
  useEffect(() => {
    if (!currentConversation || !providerRef.current) return;
    const interval = setInterval(async () => {
      const msgs = await providerRef.current!.getMessages(currentConversation);
      dispatch(setMessages({ userId: currentConversation, messages: msgs }));
    }, 60_000);
    return () => clearInterval(interval);
  }, [currentConversation, dispatch]);

  // Get current conversation messages, filtering out any locally-cached expired ones
  const currentMessages = useMemo(() => {
    if (!currentConversation) return [];
    const now = Date.now();
    return (messages[currentConversation] || []).filter(
      m => !m.expiresAt || m.expiresAt > now
    );
  }, [currentConversation, messages]);

  // Edit a message — re-encrypts with recipient's current public key
  const editMessage = useCallback(async (messageId: string, recipientId: string, newText: string): Promise<void> => {
    const keyPair = getKeyPair();
    if (!keyPair) throw new Error('No encryption keys');

    let content = newText;
    // Re-encrypt if the provider can give us the recipient's public key
    try {
      const recipientKey = await providerRef.current?.getUserPublicKey(recipientId);
      if (recipientKey) content = encryptMessage(newText, recipientKey, keyPair.secretKey);
    } catch { /* fall back to plaintext if key unavailable */ }

    await axios.put(`${API_BASE}/api/messages/${messageId}`, { content }, { withCredentials: true });

    // Emit plaintext to recipient via socket (TLS-secured, user-specific room)
    // so their handler can store it directly without decrypting
    const socket = providerRef.current?.getSocket();
    const editedAt = Date.now();
    socket?.emit('message-edited', { recipientId, messageId, newContent: newText, editedAt });

    // Update local Redux state
    if (currentConversation) {
      dispatch(setMessages({
        userId: currentConversation,
        messages: (messages[currentConversation] || []).map(m =>
          m.id === messageId ? { ...m, content: newText, editedAt } : m
        )
      }));
    }
  }, [currentConversation, messages, dispatch]);

  // Delete a message — scope: 'self' (hide for me) | 'everyone' (hard delete)
  const deleteMessage = useCallback(async (messageId: string, recipientId: string, scope: 'self' | 'everyone'): Promise<void> => {
    await axios.delete(`${API_BASE}/api/messages/${messageId}?scope=${scope}`, { withCredentials: true });

    const socket = providerRef.current?.getSocket();
    if (scope === 'everyone') {
      socket?.emit('message-deleted', { recipientId, messageId, scope });
    }

    // Remove from local Redux state
    if (currentConversation) {
      dispatch(setMessages({
        userId: currentConversation,
        messages: (messages[currentConversation] || []).filter(m => m.id !== messageId)
      }));
    }
  }, [currentConversation, messages, dispatch]);

  return {
    isConnected,
    conversations,
    currentConversation,
    messages: currentMessages,
    error,
    sendMessage,
    editMessage,
    deleteMessage,
    loadMessages,
    markAsRead,
    isUserOnline,
    deleteConversation,
    provider
  };
};
